import * as angular from 'angular';
import * as _ from 'lodash';
import ngRedux from 'ng-redux';

import { ComponentDefinition, Dashboard, AccessRights, ObjectAccessRights, Category, IGuid, ApplicationUserViewModel } from '../../../models';
import { Store, IObjectAccessRightActionsService, IDashboardActionsService, ICategoryActionsService } from '../../../stateManagement';
import { IConfigurationService, IRolesService, ICurrentApplicationService, ILoggerService, IDashboardService, ICategoryService, IapplicationUserService } from '../../../services';
import { DashboardThumbnailWidget } from '../../../widgets/dashboardThumbnail/dashboardThumbnail.widget';
import { superAdminRole, toggleEditMode } from '../../../common/';
import { mapRigths } from './mapRights';
import { MixedUserChoice, UserType } from '../../../models/mailing/MixedUserChoice';

class DashboardRightsController implements angular.IComponentController, angular.IOnDestroy {
    
    apiUrl: string;
    categories: Category[];
    choices: MixedUserChoice[];
    dashboards: DashboardThumbnailWidget[];
    formController: angular.IFormController;
    isEditMode: boolean;
    isLoading: boolean;
    loadingMessage: string;
    rights: { [guid: string]: ObjectAccessRights; };
    rightsDictionary: {
        [guid: string]: AccessRights;
    };
    roles: { id: string; name: string; }[];
    selectedChoices: MixedUserChoice[];
    selectedItem: IGuid;
    users: ApplicationUserViewModel[];

    private _pristine: AccessRights;

    private _unsubscribe: () => void;
    private loadRights: () => angular.IPromise<ObjectAccessRights[]>;
    private saveRights: (oar: ObjectAccessRights) => angular.IPromise<ObjectAccessRights[]>;
    private refreshStore: () => void;

    static $inject = [
        '$scope',
        '$ngRedux',
        '$q',
        '$translate',
        'currentApplicationService',
        'applicationUserService',
        'rolesService',
        'configurationService',
        'dashboardService',
        'categoryService',
        'dashboardActionsService',
        'objectAccessRightActionsService',
        'categoryActionsService',
        'loggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$ngRedux: ngRedux.INgRedux,
        private _$q: angular.IQService,
        private _$translate: angular.translate.ITranslateService,
        private _currentApplicationService: ICurrentApplicationService,
        private _applicationUserService: IapplicationUserService,
        private _rolesService: IRolesService,
        private _configurationService: IConfigurationService,
        private _dashboardService: IDashboardService,
        private _categoryService: ICategoryService,
        private _dashboardActionService: IDashboardActionsService,
        private _objectAccessRightActionsService: IObjectAccessRightActionsService,
        private _categoryActionsService: ICategoryActionsService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        this.apiUrl = this._configurationService.configuration.apiUrl;

        this._$q
            .all([
                this._applicationUserService.getUsers(this._currentApplicationService.getApplicationConfiguration().guid),
                this._rolesService.getAll()
            ])
            .then(responses => {
                const users = responses[0];
                const data = responses[1];
                this.users = users;
                this.roles = data.filter(d => d.name.toLowerCase() !== superAdminRole);
                this.choices = this.users
                    .map(u => {
                        return {
                            type: UserType.User,
                            name: u.name,
                            originalObject: u as any
                        };
                    })
                    .concat(this.roles.map(r => {
                        return {
                            type: UserType.Role,
                            name: r.name,
                            originalObject: r as any
                        };
                    }))
                    .sort(this.sortChoices);
            });
        this.selectedChoices = [];
        this._$scope.$on(toggleEditMode, () => {
            this.isEditMode = !this.isEditMode;
        });

        this._$scope.$watch(() => this.selectedItem, () => {
            this.selectItem(this.selectedItem, null);
        });

        this._unsubscribe = this._$ngRedux.connect((store: Store) => {
            const { dashboards, objectAccessRights, categories } = store;
            const isLoading = dashboards.isLoading
                || objectAccessRights.isLoading
                || categories.isLoading;
            let result: any;
            if (isLoading) {
                result = {
                    isLoading,
                    application: store.application
                };
            } else {
                const rights = objectAccessRights.dictionary;

                result = {
                    application: store.application,
                    isLoading,
                    rights
                };
                if (this.dashboards && this.categories) {
                    result['rightsDictionary'] = mapRigths(rights, this.dashboards, this.categories);
                }
            }
            return result;
        }, (dispatch) => {
            return {
                loadRights: () => dispatch(this._objectAccessRightActionsService.getAll()),
                saveRights: (oar: ObjectAccessRights) => dispatch(this._objectAccessRightActionsService.updateOne(oar)),
                refreshStore: () => {
                    dispatch(this._dashboardActionService.getAll(true));
                    dispatch(this._categoryActionsService.getAll(true));
                }
            };
        })(this);

        this.refresh();
    }

    $onDestroy(): void {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    private refresh() {
        this._$q.all([
            this.loadDashboards(),
            this.loadRights(),
            this.loadCategories(),
        ])
            .then(data => {
                const dashboards = this._dashboardService.getDashboardThumbnails(data[0]);
                const rights = this.rights;
                const categories = data[2];
                this.dashboards = dashboards.sort((a, b) => a.sortOrder > b.sortOrder ? 1 : -1);
                this.categories = categories.sort((a, b) => a.name > b.name ? 1 : -1);
                this.rightsDictionary = mapRigths(rights, dashboards, categories);
            });
    }

    private loadDashboards(force?: boolean): angular.IPromise<Dashboard[]> {
        return this._dashboardService.getAllUnfiltered(true);
    }

    private loadCategories(force?: boolean): angular.IPromise<Category[]> {
        return this._categoryService.getAllUnfiltered(true);
    }

    selectItem(thumbnail: IGuid, event: angular.IAngularEvent) {
        if (this.formController && this.formController.$dirty && thumbnail.guid !== this.selectedItem.guid) {
            this._loggerService.logWarning(this._$translate.instant('Save or cancel changes before selecting another item'));
        } else {
            this.selectedItem = thumbnail;
            if (this.selectedItem) {
                const rights = this.rightsDictionary[this.selectedItem.guid];
                this._pristine = angular.copy(rights);
                this.selectedChoices = rights.userCollection.map(u => _.find(this.choices, c => c.originalObject.userId === u))
                    .concat(rights.roleCollection.map(r => _.find(this.choices, c => c.name === r && c.type === UserType.Role)))
                    .sort(this.sortChoices);
            }
        }
    }

    save() {
        this.loadingMessage = this._$translate.instant('Saving access rights');
        this.isLoading = true;
        const guid = this.selectedItem.guid;
        const oar: any = angular.copy(this.rights[guid]) || { guid };
        oar.accessRights = {
            userCollection: this.selectedChoices.filter(c => c.type === UserType.User).map(u => u.originalObject.userId),
            roleCollection: this.selectedChoices.filter(c => c.type === UserType.Role).map(u => u.name)
        };
        // oar.accessRights = this.rightsDictionary[guid];
        this.saveRights(oar)
            .then(data => {
                this._loggerService.logSuccess(this._$translate.instant('Successfully saved access rights.'));
                this.isEditMode = false;
                this.formController.$setPristine();
                this.refreshStore();
                this.refresh();
                this.selectedChoices = this.selectedChoices.sort(this.sortChoices);
            })
            .catch(() => {
                this._loggerService.logError(this._$translate.instant('Failed to save access rights.'));
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    cancel() {
        this.rightsDictionary[this.selectedItem.guid] = angular.copy(this._pristine);
        this.formController.$setPristine();
        this.isEditMode = false;
    }

    private sortChoices(a: { name: string }, b: { name: string }): number {
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
    }
}

export const wtDashboardRights: ComponentDefinition = {
    componentId: 'wtDashboardRights',
    controller: DashboardRightsController,
    template: require('./dashboardRights.component.html'),
    controllerAs: 'vm',
    bindings: {
    }
};